import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { TextButton } from "@/modules/common/components/ui/text-button";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { useEditScopeOfFinishing } from "@/modules/property-tracks/hooks/scope-of-finishing/edit-scope-of-finishing.hook";
import {
  Track,
  ScopeOfFinishing,
  ScopeOfFinishingDisplay,
} from "@/modules/property-tracks/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilLine } from "lucide-react";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import z from "zod";
import {
  ToggleableRadio,
  ToggleableRadioGroup,
} from "@/modules/common/components/ui-kit/inputs/togglable-radio-group";
import { Typography } from "@mui/material";

const editScopeOfFinishingSchema = () =>
  z.object({
    scopeOfFinishing: z.nativeEnum(ScopeOfFinishing),
  });

type EditScopeOfFinishingFormFields = z.infer<
  ReturnType<typeof editScopeOfFinishingSchema>
>;

export default function EditScopeInfo({ track }: { track: Track }) {
  const [modalOpen, setModalOpen] = useState(false);

  const defaultValues = {
    scopeOfFinishing: (track.scopeOfFinishing as ScopeOfFinishing) || undefined,
  };

  const { handleSubmit, reset, control } =
    useForm<EditScopeOfFinishingFormFields>({
      resolver: zodResolver(editScopeOfFinishingSchema()),
      defaultValues,
    });

  const onSubmit: SubmitHandler<EditScopeOfFinishingFormFields> = async (
    data,
  ) => {
    submitEditScope(data);
  };

  const mutation = useEditScopeOfFinishing();

  const submitEditScope = (data: EditScopeOfFinishingFormFields) => {
    mutation.mutate(
      {
        uid: track.uid,
        data: data,
      },
      {
        onSuccess: () => {
          setModalOpen(false);
          reset();
          showToast({
            type: ToastType.SUCCESS,
            message: "Scope updated successfully",
            toastId: TOAST_IDS.EDIT_SCOPE_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_SCOPE_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    reset(defaultValues);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };
  return (
    <>
      <TextButton
        onClick={handleModalOpen}
        sx={{ display: "flex", gap: 0.5, alignItems: "bottom" }}
      >
        <PencilLine size={16} /> Edit
      </TextButton>

      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Edit Scope"
        width={480}
        handleConfirm={handleSubmit(onSubmit)}
        handleCloseModal={handleCloseModal}
        isLoading={mutation.isPending}
      >
        <Controller
          control={control}
          name="scopeOfFinishing"
          render={({ field, fieldState }) => (
            <div>
              <ToggleableRadioGroup
                value={field.value}
                onChange={(value) => field.onChange(value)}
              >
                {Object.values(ScopeOfFinishing).map((scopeOfFinishing) => (
                  <ToggleableRadio
                    key={scopeOfFinishing}
                    value={scopeOfFinishing}
                    label={ScopeOfFinishingDisplay[scopeOfFinishing]}
                  />
                ))}
              </ToggleableRadioGroup>
              {fieldState.error && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {fieldState.error?.message}
                </Typography>
              )}
            </div>
          )}
        />
      </EditFieldModal>
    </>
  );
}
